import { API } from 'src/api';
import { ErrorCodes } from 'src/data/error-codes';

import { pageViews } from '../assets/data';
import { HeaderData } from '../types/file';

export const useFileService = () => {
  async function fetchFileDetails({ id, isPublic = false }: { id: string; isPublic?: boolean }) {
    try {
      const response = await API.get(`/files${isPublic ? '/public' : ''}?id=${id}`);

      if (response.data.success) {
        return formatFileResponse(response.data.data.file);
      }
    } catch (error) {
      console.error('errror', error);
      if (error.response.data.data) return error.response.data.data;
    }
    return null;
  }

  async function updatePublicFilePassword({ id, password }: { id: string; password?: string }) {
    try {
      const response = await API.post(`/files/public?id=${id}`, { password });

      if (response.data.success) {
        return {
          file: formatFileResponse(response.data.data.file),
          publicPassword: response.data.data.public_pwd,
        };
      } else if (response.data.data === ErrorCodes['FILE_PASSWORD_MISMATCH']) {
        return 'incorrect-password';
      }
    } catch (error) {
      console.error('errror', error);

      return null;
    }
  }

  async function pinFile({ fileId, pin }: { fileId: string; pin: boolean }) {
    try {
      const response = await API.put(`/files/pin`, {
        id: fileId,
        pin: pin,
      });
      if (response.data.success) {
        return formatFileResponse(response.data.data.file);
      }
    } catch (error) {
      console.error('errror', error);
      return null;
    }
  }
  async function favoriteFile({ fileId }: { fileId: string }) {
    try {
      const response = await API.put(`/files/favorite/${fileId}`);
      if (response.data.success) {
        return formatFileResponse(response.data.data.file);
      }
    } catch (error) {
      console.error('errror', error);
      return null;
    }
  }

  async function resetFileOnboarding({ fileId }: { fileId: string }) {
    try {
      const response = await API.put(`/files/resetFileOnboarding/${fileId}`);
      if (response.data.success) {
        return formatFileResponse(response.data.data.file);
      }
    } catch (error) {
      console.error('errror', error);
      return null;
    }
  }
  async function updateFileNameAndIcon({ name, icon, fileId }) {
    try {
      const response = await API.put(`/files`, {
        id: fileId,
        title: name,
        icon,
      });
      if (response.data.success) {
        return formatFileResponse(response.data.data.file);
      }
    } catch (error) {
      console.error('errror', error);
      return null;
    }
  }
  async function updateCoverImage({ name, icon, fileId }) {
    try {
      const response = await API.put(`/files`, {
        id: fileId,
        title: name,
        icon,
      });
      if (response.data.success) {
        return formatFileResponse(response.data.data.file);
      }
    } catch (error) {
      console.error('errror', error);
      return null;
    }
  }

  async function addFileView({ fileId, type }: { fileId: string; type: string }) {
    try {
      const payload = pageViews.includes(type)
        ? { parent: fileId, filetype: type }
        : { parent: fileId, filetype: 'board', view: type };
      const response = await API.post(`/files/view`, payload);

      if (response.data.success) {
        return formatFileResponse(response.data.data.file);
      }
    } catch (error) {
      console.error('errror', error);
      return null;
    }
  }
  async function deleteFileView({ fileId, viewId }: { fileId: string; viewId: string }) {
    try {
      const response = await API.delete(`/files/view?file=${fileId}&view=${viewId}`);
      if (response.data.success) {
        return formatFileResponse(response.data.data.file);
      }
    } catch (error) {
      console.error('errror', error);
      return null;
    }
  }

  async function togglePublicView(fileId: string, makePublic = true) {
    try {
      const response = await API.post('/files/public', {
        id: fileId,
        is_public: makePublic,
      });
      return response;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async function addCover({ fileId, coverImage }: { fileId: string; coverImage: string }) {
    try {
      const response = await API.put(`/files`, {
        id: fileId,
        showCover: true,
        coverImage,
      });
      return response;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async function disableCover({ fileId }: { fileId: string }) {
    try {
      const response = await API.put(`/files`, {
        id: fileId,
        showCover: false,
      });
      return response;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async function addTag({
    fileId,
    name,
    color,
    id,
  }: {
    name: string;
    id: string;
    color: string;
    fileId: string;
  }) {
    try {
      const response = await API.post(`files/tags`, {
        name,
        color,
        file: fileId,
        tagId: id,
      });

      return formatFileResponse(response.data.data.file);
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async function updateTag({
    fileId,
    name,
    color,
    id,
    mode,
  }: {
    name?: string;
    id?: string;
    color?: string;
    fileId: string;
    mode: string;
  }) {
    try {
      const response = await API.put(`/files/tags`, {
        name,
        color,
        file: fileId,
        id: id,
        mode,
      });
      return formatFileResponse(response.data.data.file);
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async function deleteTag({ id, fileId }: { id: string; fileId: string }) {
    try {
      const response = await API.delete(`/files/tags?id=${id}&file=${fileId}`);
      return formatFileResponse(response.data.data.file);
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  const getRecentIcons = async () => {
    try {
      const response = await API.get(`/file/emoji`);
      if (response.data.success) {
        return response.data.data;
      }
    } catch (error) {
      console.error(error);
    }
    return false;
  };

  const updateRecentIcon = async (imgUrl: string) => {
    try {
      const response = await API.post(`/file/emoji`, { src: imgUrl });
      if (response.data.success) {
        return true;
      }
    } catch (error) {
      console.error(error);
    }
    return false;
  };

  const getRecentUploads = async () => {
    try {
      const response = await API.get(`/file/cover`);
      if (response.data.success) {
        return response.data.data;
      }
    } catch (error) {
      console.error(error);
    }
    return false;
  };

  const importcsvFile = async ({
    workspace,
    fileTitle,
    tasknameIndex,
    fileData,
    fileHeader,
    fileType,
  }: {
    workspace?: string;
    fileTitle: string;
    tasknameIndex: number;
    fileData: [[string]];
    fileHeader: HeaderData[];
    fileType?: string;
  }) => {
    try {
      const response = await API.post(`/files/import`, {
        workspace: workspace,
        title: fileTitle,
        index: tasknameIndex,
        fields: fileHeader,
        file: fileData,
        type: fileType,
      });
      if (response.data.success) {
        return response.data.data;
      }
    } catch (error) {
      console.error(error);
    }
    return false;
  };

  const updateEmbedUrl = async (id: string, url: string) => {
    try {
      const response = await API.put(`/embed`, {
        id,
        url,
      });
      if (response.data.success) {
        return response.data.data;
      }
    } catch (error) {
      console.error(error);
    }
    return false;
  };

  const renameView = async (file: string, view: string, title: string) => {
    try {
      const response = await API.put('/files/view', { file, view, title });
      if (response.data.success) {
        return formatFileResponse(response.data.data.file);
      }
    } catch (error) {
      console.error(error);
    }
    return false;
  };

  const fetchManageViewData = async (fileId: string) => {
    try {
      const response = await API.get(`files/access?id=${fileId}`);
      if (response.data.success) {
        return response;
      }
    } catch (error) {
      console.error(error);
    }
    return false;
  };

  const fetchBoardFiles = async ({
    mode = '',
    filter = '',
    sort = '',
    page = 0,
    itemLimit = 20,
    workspace = '',
    search = '',
  } = {}) => {
    try {
      const response = await API.get(
        `/files/task?mode=${mode}&filter=${filter}&sort=${sort}&page=${page}&limit=${itemLimit}&workspace=${workspace}&search=${search}`
      );
      if (response.data.success) {
        return response;
      }
    } catch (error) {
      console.error(error);
    }
    return false;
  };

  const saveViewConfiguration = async ({
    viewId,
    fileId,
    data,
  }: {
    viewId: string;
    fileId: string;
    data: any;
  }) => {
    try {
      const apiData = {
        file: fileId,
        config: data,
      };
      if (viewId) {
        apiData['view'] = viewId;
      }
      const response = await API.post(`/files/list/config`, apiData);
      if (response.data.success) {
        return response.data.data;
      }
    } catch (error) {
      console.error(error);
    }
    return false;
  };

  return {
    fetchFileDetails,
    pinFile,
    favoriteFile,
    resetFileOnboarding,
    addFileView,
    deleteFileView,
    updateFileNameAndIcon,
    togglePublicView,
    getRecentUploads,
    updateRecentIcon,
    getRecentIcons,
    disableCover,
    addCover,
    updateCoverImage,
    addTag,
    deleteTag,
    updateTag,
    importcsvFile,
    updateEmbedUrl,
    renameView,
    updatePublicFilePassword,
    fetchManageViewData,
    fetchBoardFiles,
    saveViewConfiguration,
  };
};
export function formatFileResponse(fileData): any {
  return {
    ...fileData,
    name: fileData.title || fileData?.file?.name || fileData?.file?.title,
    icon: fileData?.icon || fileData.file?.icon,
    id: fileData._id,
    isPublic: fileData.isPublic,
    isProtected: fileData.is_protected,
    createdAt: fileData.createdAt,
    createdBy: fileData.owner && fileData.owner.firstname,
    tags: fileData?.tags?.map((item) => {
      return {
        ...item,
        id: item?._id,
      };
    }),
    organisationTags: fileData?.orgTags?.map((item) => {
      return {
        ...item,
        id: item?._id,
        isChecked: fileData?.tags?.some((tag) => tag._id === item._id),
      };
    }),
    favorite: fileData.favorite,
    resetFileOnboarding: fileData.resetFileOnboarding,
    authorisation: true,
    owner: fileData.owner,
    coverImage: fileData.coverImage,
    showCover: fileData.showCover,
    pin: fileData.pin,
    access: fileData.access,
    workspace:
      fileData?.workspace?.name === 'default-workspace'
        ? {}
        : {
            name: fileData.workspace?.name,
            id: fileData.workspace?.workspaceId,
            icon: fileData.workspace?.icon,
            members: fileData.workspace?.members,
          },
    shared: fileData.shared,

    updatedAt: fileData.updatedAt,
    updatedBy: fileData.updatedBy,
    primaryFile: {
      id: fileData?.filetype === 'agile' ? fileData._id : fileData.file?._id,
      type: fileData?.filetype === 'creation' ? 'document' : fileData?.filetype,
      view: fileData?.view,
      name: fileData?.title,
      data: {
        url: fileData?.file?.url,
      },
      config: fileData?.config,
    },
    membersWithAccess: fileData?.workspace?.members || [],
    modified: fileData.modified,
    internalFiles: fileData?.views?.map((item) => {
      return {
        type: item?.filetype === 'creation' ? 'document' : item?.filetype,
        view: item?.view,
        name: item?.title,
        id: item?.file,
        viewId: item?._id,
        data: {
          url: item?.url,
        },
        config: item?.config,
      };
    }),
  };
}
